import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Trash2,
  Save,
  Ship,
  User,
  Anchor,
  Package,
  MapPin,
  AlertCircle,
  ArrowRight,
  Send,
  Info,
  Warehouse,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Autocomplete from '@/components/Autocomplete';
import { useToast } from '@/components/ui/toaster.jsx';
import {
  getCountriesWithSeaports,
  getSeaportsByPais,
  searchClients,
  getAsesores,
  getActiveIncoterms,
  getActiveCommodityTypes,
} from '../Services/lclCatalogsService.js';
import { calcularTotalesLcl } from '../utils/lclFormatters.js';
import {
  TIPOS_SERVICIO,
  TIPOS_BULTO,
  MONEDAS_VALOR_MERCANCIA,
  UNIDADES_DIMENSION,
  UNIDADES_PESO,
  INCOTERM_CON_RECOGIDA,
  requierePickup,
  requiereDelivery,
} from '../utils/lclBoardConstants.js';

/**
 * Formulario de Solicitud LCL (carga marítima consolidada).
 *
 * Es el gemelo de `components/RequestForm.jsx` (aéreo), pero con la
 * lógica marítima:
 *  - Ruta por PUERTOS (UN/LOCODE) en lugar de aeropuertos (IATA).
 *  - `tipoServicio` (Puerto/Puerta) que habilita las ciudades de
 *    recolección y entrega.
 *  - Totales en CBM (m³), toneladas y unidades W/M en vez de peso
 *    volumétrico IATA. La fórmula es la de `utils/lclFormatters.js`,
 *    la misma que recalcula el backend.
 *  - Cada bulto tiene tipo de bulto y datos IMO/IMDG.
 *
 * Modos de render:
 *  - normal (default): layout con SectionCard, header propio,
 *    submit al final.
 *  - compact: igual que normal pero con menos padding.
 *  - modal: layout denso, sin SectionCard envolventes, con header
 *    sticky y footer sticky. En este modo el botón submit se
 *    deshabilita hasta que el form esté completo (`isFormComplete`).
 *
 * Props:
 *  - onSubmit(payload), onCancel(), submitting, submitLabel,
 *    cancelLabel, showCancelButton, hideHeader, compact, modal
 */
export default function LclRequestForm({
  onSubmit,
  onCancel,
  submitting = false,
  submitLabel = 'Guardar solicitud',
  cancelLabel = 'Cancelar',
  showCancelButton = true,
  hideHeader = false,
  compact = false,
  modal = false,
  modalTitle = 'Crear Solicitud LCL',
  modalSubtitle = 'Complete todos los campos requeridos',
}) {
  const toast = useToast();

  // ============= Estado del formulario =============
  const [form, setForm] = useState({
    tipoOperacion: '',
    tipoServicio: 'Puerto a Puerto',
    requiereOTM: false,
    incotermId: '',
    direccionRecogidaOrigen: '',
    valorMercancia: '',
    monedaValorMercancia: 'USD',
    descripcion: '',
    clienteId: '',
    clienteLabel: '',
    asesorId: '',
    paisOrigenId: '',
    paisOrigenLabel: '',
    puertoCargaId: '',
    puertoCargaLabel: '',
    ciudadOrigen: '',
    paisDestinoId: '',
    paisDestinoLabel: '',
    puertoDescargaId: '',
    puertoDescargaLabel: '',
    ciudadDestino: '',
  });

  const [incoterms, setIncoterms] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [commodityTypes, setCommodityTypes] = useState([]);

  const [seaportsOrigen, setSeaportsOrigen] = useState([]);
  const [seaportsDestino, setSeaportsDestino] = useState([]);
  const [loadingSeaportsOrigen, setLoadingSeaportsOrigen] = useState(false);
  const [loadingSeaportsDestino, setLoadingSeaportsDestino] = useState(false);

  const emptyDetalle = () => ({
    numPiezas: '',
    tipoBulto: 'Caja',
    largo: '',
    ancho: '',
    alto: '',
    unidadDimension: 'cm',
    peso: '',
    unidadPeso: 'kg',
    tipoMercanciaId: '',
    apilable: 'Si',
    imo: false,
    imoClase: '',
    unNumber: '',
  });
  const [detalles, setDetalles] = useState([emptyDetalle()]);

  const [errores, setErrores] = useState({});

  // ============= Totales calculados =============
  // Misma fórmula que el backend: el servidor recalcula y rechaza la
  // solicitud si el volumen difiere más de un 1%.
  const totales = useMemo(() => calcularTotalesLcl(detalles), [detalles]);

  const pickup = requierePickup(form.tipoServicio);
  const delivery = requiereDelivery(form.tipoServicio);

  /**
   * Con EXW la mercancía se retira de la bodega del vendedor, así que
   * el operador necesita la dirección exacta de recogida. Se resuelve
   * por el `codigo` del incoterm seleccionado, no por su id.
   */
  const incotermSeleccionado = useMemo(
    () => incoterms.find((i) => i._id === form.incotermId) || null,
    [incoterms, form.incotermId],
  );
  const esEXW =
    String(incotermSeleccionado?.codigo || '').trim().toUpperCase() ===
    INCOTERM_CON_RECOGIDA;

  // ============= Carga inicial de catálogos =============
  useEffect(() => {
    (async () => {
      try {
        const [incs, ase, cmts] = await Promise.all([
          getActiveIncoterms(),
          getAsesores(),
          getActiveCommodityTypes(),
        ]);
        setIncoterms(incs);
        setAsesores(ase.data || ase || []);
        setCommodityTypes(cmts);
      } catch (e) {
        toast.error('Error al cargar catálogos', e?.message);
      }
    })();
  }, [toast]);

  // ============= Puertos por país =============
  const loadSeaports = useCallback(
    async (paisNombre, side) => {
      if (!paisNombre) return;
      const setter = side === 'origen' ? setSeaportsOrigen : setSeaportsDestino;
      const loading =
        side === 'origen' ? setLoadingSeaportsOrigen : setLoadingSeaportsDestino;
      loading(true);
      try {
        const r = await getSeaportsByPais(paisNombre, 'Activo');
        setter(r?.data || []);
      } catch (e) {
        toast.error('Error al cargar puertos', e?.message);
        setter([]);
      } finally {
        loading(false);
      }
    },
    [toast],
  );

  // ============= Helpers =============
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const clearError = (key) =>
    setErrores((e) => {
      const n = { ...e };
      delete n[key];
      return n;
    });

  // ============= Handlers de Autocomplete =============
  const handleClientChange = (id, item) => {
    setForm((f) => ({
      ...f,
      clienteId: id || '',
      clienteLabel: item ? `${item.nombre} — ${item.nit}` : '',
    }));
    clearError('clienteId');
  };
  const handlePaisOrigenChange = (id, item) => {
    const paisNombre = item?.nombre || item?._id || '';
    setForm((f) => ({
      ...f,
      paisOrigenId: paisNombre,
      paisOrigenLabel: paisNombre,
      puertoCargaId: '',
      puertoCargaLabel: '',
    }));
    setSeaportsOrigen([]);
    if (paisNombre) loadSeaports(paisNombre, 'origen');
    clearError('paisOrigenId');
  };
  const handlePaisDestinoChange = (id, item) => {
    const paisNombre = item?.nombre || item?._id || '';
    setForm((f) => ({
      ...f,
      paisDestinoId: paisNombre,
      paisDestinoLabel: paisNombre,
      puertoDescargaId: '',
      puertoDescargaLabel: '',
    }));
    setSeaportsDestino([]);
    if (paisNombre) loadSeaports(paisNombre, 'destino');
    clearError('paisDestinoId');
  };
  const handlePuertoCargaChange = (id) => {
    setField('puertoCargaId', id || '');
    const item = seaportsOrigen.find((p) => p._id === id);
    setField('puertoCargaLabel', item ? `${item.unlocode} - ${item.nombre}` : '');
    clearError('puertoCargaId');
  };
  const handlePuertoDescargaChange = (id) => {
    setField('puertoDescargaId', id || '');
    const item = seaportsDestino.find((p) => p._id === id);
    setField('puertoDescargaLabel', item ? `${item.unlocode} - ${item.nombre}` : '');
    clearError('puertoDescargaId');
  };

  // ============= Detalle de bultos =============
  const addDetalle = () => setDetalles((d) => [...d, emptyDetalle()]);
  const removeDetalle = (idx) => {
    setDetalles((d) => (d.length > 1 ? d.filter((_, i) => i !== idx) : d));
  };
  const updateDetalle = (idx, key, value) => {
    setDetalles((d) => d.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  // ============= Campos faltantes (fuente única) =============
  /**
   * Lista legible de lo que falta por diligenciar. Es la ÚNICA fuente
   * de verdad: de acá salen la leyenda roja, el estado del botón de
   * guardar y el aviso al intentar enviar. Así no se pueden desfasar
   * entre sí (que era el riesgo de tener `validate()` y
   * `isFormComplete` con listas paralelas).
   */
  const camposFaltantes = useMemo(() => {
    const faltan = [];
    if (!form.tipoOperacion) faltan.push('Tipo de operación');
    if (!form.tipoServicio) faltan.push('Tipo de servicio');
    if (!form.incotermId) faltan.push('Incoterm');
    if (esEXW && !form.direccionRecogidaOrigen.trim()) {
      faltan.push('Dirección de recogida en origen (obligatoria con EXW)');
    }
    if (!form.clienteId) faltan.push('Cliente');
    if (!form.asesorId) faltan.push('Asesor comercial');
    if (!form.paisOrigenId) faltan.push('País de origen');
    if (!form.puertoCargaId) faltan.push('Puerto de carga');
    if (pickup && !form.ciudadOrigen.trim()) faltan.push('Ciudad de recolección');
    if (!form.paisDestinoId) faltan.push('País de destino');
    if (!form.puertoDescargaId) faltan.push('Puerto de descarga');
    if (delivery && !form.ciudadDestino.trim()) faltan.push('Ciudad de entrega');

    if (detalles.length === 0) {
      faltan.push('Agregá al menos un bulto en el detalle');
    }
    detalles.forEach((d, i) => {
      const f = [];
      if (!d.numPiezas || Number(d.numPiezas) <= 0) f.push('bultos');
      if (!d.largo || Number(d.largo) <= 0) f.push('largo');
      if (!d.ancho || Number(d.ancho) <= 0) f.push('ancho');
      if (!d.alto || Number(d.alto) <= 0) f.push('alto');
      if (!d.peso || Number(d.peso) <= 0) f.push('peso x unidad');
      if (!d.tipoMercanciaId) f.push('tipo de mercancía');
      if (d.imo && !String(d.imoClase || '').trim()) f.push('clase IMO');
      if (f.length) faltan.push(`Bulto #${i + 1}: ${f.join(', ')}`);
    });

    // Salvaguarda típica: dimensiones tecleadas en metros con la
    // unidad en cm dan un volumen que redondea a 0.
    if (detalles.length > 0 && totales.volumenTotal <= 0) {
      faltan.push(
        'El volumen (m³) da 0 — revisá las dimensiones y la unidad (cm / m / in)',
      );
    }
    return faltan;
  }, [form, detalles, totales, pickup, delivery, esEXW]);

  const isFormComplete = camposFaltantes.length === 0;

  // ============= Validación (marca los campos en rojo) =============
  const validate = () => {
    const err = {};
    if (!form.tipoOperacion) err.tipoOperacion = 'Selecciona el tipo de operación';
    if (!form.tipoServicio) err.tipoServicio = 'Selecciona el tipo de servicio';
    if (!form.incotermId) err.incotermId = 'Selecciona un Incoterm';
    if (esEXW && !form.direccionRecogidaOrigen.trim()) {
      err.direccionRecogidaOrigen =
        'Con EXW la carga se retira en origen: indica la dirección';
    }
    if (!form.clienteId) err.clienteId = 'Selecciona un cliente';
    if (!form.asesorId) err.asesorId = 'Selecciona un asesor comercial o "No asignado"';
    if (!form.paisOrigenId) err.paisOrigenId = 'Selecciona el país de origen';
    if (!form.puertoCargaId) err.puertoCargaId = 'Selecciona el puerto de carga';
    if (!form.paisDestinoId) err.paisDestinoId = 'Selecciona el país de destino';
    if (!form.puertoDescargaId) err.puertoDescargaId = 'Selecciona el puerto de descarga';
    if (pickup && !form.ciudadOrigen.trim()) {
      err.ciudadOrigen = 'El servicio empieza en puerta: indica la ciudad de recolección';
    }
    if (delivery && !form.ciudadDestino.trim()) {
      err.ciudadDestino = 'El servicio termina en puerta: indica la ciudad de entrega';
    }
    if (totales.numPiezas <= 0) err.numPiezas = 'El detalle debe sumar al menos 1 bulto';
    if (totales.pesoTotal <= 0) err.pesoTotal = 'El peso total debe ser mayor a 0';
    if (totales.volumenTotal <= 0) {
      err.volumenTotal = 'El volumen da 0: revisá las dimensiones y la unidad';
    }

    const detErrors = detalles.map((d) => {
      const e = {};
      if (!d.numPiezas || Number(d.numPiezas) <= 0) e.numPiezas = 'Requerido';
      if (!d.largo || Number(d.largo) <= 0) e.largo = 'Requerido';
      if (!d.ancho || Number(d.ancho) <= 0) e.ancho = 'Requerido';
      if (!d.alto || Number(d.alto) <= 0) e.alto = 'Requerido';
      if (!d.peso || Number(d.peso) <= 0) e.peso = 'Requerido';
      if (!d.tipoMercanciaId) e.tipoMercanciaId = 'Requerido';
      if (d.imo && !String(d.imoClase || '').trim()) e.imoClase = 'Requerido';
      return e;
    });
    if (detErrors.some((e) => Object.keys(e).length > 0)) err.detalles = detErrors;
    if (detalles.length === 0) err.detallesGeneral = 'Agrega al menos una fila en el detalle';
    setErrores(err);
    return Object.keys(err).length === 0;
  };

  /**
   * Leyenda roja con lo que falta. Se muestra junto al botón de
   * guardar, que es donde el usuario se pregunta por qué no puede
   * enviar el formulario.
   */
  const renderCamposFaltantes = () => {
    if (camposFaltantes.length === 0) return null;
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
        <div className="flex items-center gap-1.5 text-xs font-semibold">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          Faltan {camposFaltantes.length} campo(s) por diligenciar
        </div>
        <ul className="mt-1 ml-5 list-disc text-xs space-y-0.5">
          {camposFaltantes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </div>
    );
  };

  // ============= Submit =============
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) {
      toast.error('Faltan datos', 'Revisa los campos marcados en rojo antes de guardar');
      return;
    }
    const payload = {
      tipoOperacion: form.tipoOperacion,
      tipoServicio: form.tipoServicio,
      incotermId: form.incotermId,
      descripcion: form.descripcion || undefined,
      // Opcional: solo se manda si el operador lo diligenció.
      valorMercancia:
        form.valorMercancia === '' ? undefined : Number(form.valorMercancia),
      monedaValorMercancia:
        form.valorMercancia === '' ? undefined : form.monedaValorMercancia,
      clienteId: form.clienteId,
      // Si eligió "No asignado" mandamos null para que el backend no
      // asigne un asesor. Si es un id real, lo mandamos tal cual.
      asesorId: form.asesorId === '__NONE__' ? null : form.asesorId,
      paisOrigenId: form.paisOrigenId,
      puertoCargaId: form.puertoCargaId,
      ciudadOrigen: pickup ? form.ciudadOrigen.trim() : undefined,
      paisDestinoId: form.paisDestinoId,
      puertoDescargaId: form.puertoDescargaId,
      ciudadDestino: delivery ? form.ciudadDestino.trim() : undefined,
      // Los totales van para que el backend los contraste; él manda.
      numPiezas: totales.numPiezas,
      pesoTotal: totales.pesoTotal,
      volumenTotal: totales.volumenTotal,
      detalles: detalles.map((d) => ({
        numPiezas: Number(d.numPiezas),
        tipoBulto: d.tipoBulto,
        largo: Number(d.largo),
        ancho: Number(d.ancho),
        alto: Number(d.alto),
        unidadDimension: d.unidadDimension,
        peso: Number(d.peso),
        unidadPeso: d.unidadPeso,
        tipoMercanciaId: d.tipoMercanciaId,
        apilable: d.apilable,
        imo: !!d.imo,
        imoClase: d.imo ? String(d.imoClase || '').trim() || undefined : undefined,
        unNumber: d.imo ? String(d.unNumber || '').trim() || undefined : undefined,
      })),
    };
    await onSubmit?.(payload);
  };

  // === Sub-componentes reutilizables ===
  const selectClass = (hasError) =>
    `flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
      hasError ? 'border-destructive' : 'border-input'
    }`;

  const renderFieldLabel = (text, required) => (
    <Label className="text-sm font-medium text-foreground">
      {text}
      {required && <span className="text-destructive"> *</span>}
    </Label>
  );

  const renderFieldError = (key) => (errores[key] ? <FieldError msg={errores[key]} /> : null);

  // 1) Información General
  const renderInfoGeneral = () => (
    <SectionBlock icon={Info} title="Información General" number={1} modal={modal}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('Tipo de operación', true)}
          <select
            value={form.tipoOperacion}
            onChange={(e) => {
              setField('tipoOperacion', e.target.value);
              clearError('tipoOperacion');
            }}
            className={selectClass(errores.tipoOperacion)}
          >
            <option value="">— Selecciona —</option>
            <option value="Importación">Importación</option>
            <option value="Exportación">Exportación</option>
          </select>
          {renderFieldError('tipoOperacion')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Tipo de servicio', true)}
          <select
            value={form.tipoServicio}
            onChange={(e) => {
              setField('tipoServicio', e.target.value);
              clearError('tipoServicio');
            }}
            className={selectClass(errores.tipoServicio)}
          >
            {TIPOS_SERVICIO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground">
            Define si hay recolección y/o entrega en la puerta del cliente.
          </p>
          {renderFieldError('tipoServicio')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Incoterm', true)}
          <select
            value={form.incotermId}
            onChange={(e) => {
              setField('incotermId', e.target.value);
              clearError('incotermId');
            }}
            className={selectClass(errores.incotermId)}
          >
            <option value="">— Selecciona —</option>
            {incoterms.map((i) => (
              <option key={i._id} value={i._id}>
                {i.codigo} - {i.nombre}
              </option>
            ))}
          </select>
          {renderFieldError('incotermId')}
        </div>
      </div>
      {/* OTM — servicio adicional que el cliente puede requerir */}
      <div className="mt-3">
        <label
          className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
            form.requiereOTM
              ? 'border-primary bg-primary/5'
              : 'border-input hover:bg-muted/40'
          }`}
        >
          <input
            type="checkbox"
            checked={form.requiereOTM}
            onChange={(e) => setField('requiereOTM', e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <span>
            <span className="text-sm font-medium text-foreground">
              Requiere OTM (Operador de Transporte Multimodal)
            </span>
            <span className="block text-[11px] text-muted-foreground">
              Marcar si el cliente también necesita que el operador asuma el
              transporte multimodal bajo un único documento.
            </span>
          </span>
        </label>
      </div>

      {/* Dirección de recogida — obligatoria con EXW */}
      {esEXW && (
        <div className="space-y-1.5 mt-3 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-900/10 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
            <Warehouse className="h-3.5 w-3.5" />
            Incoterm EXW: la carga se retira en la bodega del vendedor
          </div>
          {renderFieldLabel('Dirección de recogida en origen', true)}
          <Input
            value={form.direccionRecogidaOrigen}
            onChange={(e) => {
              setField('direccionRecogidaOrigen', e.target.value);
              clearError('direccionRecogidaOrigen');
            }}
            maxLength={250}
            placeholder="Calle, número, bodega, ciudad y país de recogida"
            className={errores.direccionRecogidaOrigen ? 'border-destructive' : ''}
          />
          {renderFieldError('direccionRecogidaOrigen')}
        </div>
      )}


      {/* Valor de la mercancía — opcional. Se usa para cotizar el
          seguro y para los trámites aduaneros. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
        <div className="space-y-1.5 md:col-span-2">
          {renderFieldLabel('Valor aproximado de la mercancía')}
          <Input
            type="number"
            step="0.01"
            min={0}
            value={form.valorMercancia}
            onChange={(e) => setField('valorMercancia', e.target.value)}
            placeholder="Opcional — sirve para cotizar el seguro"
          />
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Moneda')}
          <select
            value={form.monedaValorMercancia}
            onChange={(e) => setField('monedaValorMercancia', e.target.value)}
            className={selectClass(false)}
          >
            {MONEDAS_VALOR_MERCANCIA.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1.5 mt-3">
        {renderFieldLabel('Descripción')}
        <textarea
          rows={modal ? 1 : 3}
          maxLength={500}
          value={form.descripcion}
          onChange={(e) => setField('descripcion', e.target.value)}
          placeholder="Detalle adicional sobre la operación (opcional, máx. 500 caracteres)"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        {!modal && (
          <div className="text-xs text-muted-foreground text-right">
            {form.descripcion.length}/500
          </div>
        )}
      </div>
    </SectionBlock>
  );

  // 2) Cliente y Asesor
  const renderClienteAsesor = () => (
    <SectionBlock icon={User} title="Cliente y Asesor" number={2} modal={modal}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('Cliente', true)}
          <Autocomplete
            fetchOptions={async (q) => {
              const r = await searchClients(q);
              return r?.data || [];
            }}
            value={form.clienteId}
            onChange={handleClientChange}
            placeholder="Buscar por Nombre o NIT…"
            getOptionLabel={(item) => `${item.nombre} — ${item.nit}`}
            error={errores.clienteId}
            required
          />
          {form.clienteLabel && (
            <div className="text-xs text-muted-foreground">
              Seleccionado: {form.clienteLabel}
            </div>
          )}
          {renderFieldError('clienteId')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Asesor Comercial', true)}
          <select
            value={form.asesorId}
            onChange={(e) => {
              setField('asesorId', e.target.value);
              clearError('asesorId');
            }}
            className={selectClass(errores.asesorId)}
          >
            <option value="">— Selecciona —</option>
            <option value="__NONE__">— No asignado —</option>
            {asesores.map((a) => (
              <option key={a._id} value={a._id}>
                {a.nombre}
              </option>
            ))}
          </select>
          {renderFieldError('asesorId')}
        </div>
      </div>
    </SectionBlock>
  );

  // 3) Ruta Marítima
  const renderRutaMaritima = () => (
    <SectionBlock icon={MapPin} title="Ruta Marítima" number={3} modal={modal}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Origen */}
        <div className="space-y-3 rounded-lg border border-border p-3 bg-card">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
            <Anchor className="h-3.5 w-3.5" /> Origen
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('País de origen', true)}
            <Autocomplete
              fetchOptions={async (q) => {
                const r = await getCountriesWithSeaports(q);
                return r?.data || [];
              }}
              value={form.paisOrigenId}
              onChange={handlePaisOrigenChange}
              placeholder="Escribe el país…"
              getOptionLabel={(item) => item.nombre || item._id || ''}
              error={errores.paisOrigenId}
              required
            />
            {renderFieldError('paisOrigenId')}
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('Puerto de carga', true)}
            <select
              value={form.puertoCargaId}
              onChange={(e) => handlePuertoCargaChange(e.target.value)}
              disabled={!form.paisOrigenId || loadingSeaportsOrigen}
              className={selectClass(errores.puertoCargaId) + ' disabled:opacity-50'}
            >
              <option value="">
                {!form.paisOrigenId
                  ? 'Primero selecciona un país'
                  : loadingSeaportsOrigen
                    ? 'Cargando puertos…'
                    : seaportsOrigen.length === 0
                      ? 'Sin puertos activos en este país'
                      : '— Selecciona —'}
              </option>
              {seaportsOrigen.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.unlocode} - {p.nombre}
                </option>
              ))}
            </select>
            {renderFieldError('puertoCargaId')}
          </div>
          {pickup && (
            <div className="space-y-1.5">
              {renderFieldLabel('Ciudad de recolección', true)}
              <Input
                value={form.ciudadOrigen}
                onChange={(e) => {
                  setField('ciudadOrigen', e.target.value);
                  clearError('ciudadOrigen');
                }}
                placeholder="Dónde se recoge la carga"
                className={errores.ciudadOrigen ? 'border-destructive' : ''}
              />
              {renderFieldError('ciudadOrigen')}
            </div>
          )}
        </div>

        {/* Destino */}
        <div className="space-y-3 rounded-lg border border-border p-3 bg-card">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
            <ArrowRight className="h-3.5 w-3.5" /> Destino
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('País de destino', true)}
            <Autocomplete
              fetchOptions={async (q) => {
                const r = await getCountriesWithSeaports(q);
                return r?.data || [];
              }}
              value={form.paisDestinoId}
              onChange={handlePaisDestinoChange}
              placeholder="Escribe el país…"
              getOptionLabel={(item) => item.nombre || item._id || ''}
              error={errores.paisDestinoId}
              required
            />
            {renderFieldError('paisDestinoId')}
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('Puerto de descarga', true)}
            <select
              value={form.puertoDescargaId}
              onChange={(e) => handlePuertoDescargaChange(e.target.value)}
              disabled={!form.paisDestinoId || loadingSeaportsDestino}
              className={selectClass(errores.puertoDescargaId) + ' disabled:opacity-50'}
            >
              <option value="">
                {!form.paisDestinoId
                  ? 'Primero selecciona un país'
                  : loadingSeaportsDestino
                    ? 'Cargando puertos…'
                    : seaportsDestino.length === 0
                      ? 'Sin puertos activos en este país'
                      : '— Selecciona —'}
              </option>
              {seaportsDestino.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.unlocode} - {p.nombre}
                </option>
              ))}
            </select>
            {renderFieldError('puertoDescargaId')}
          </div>
          {delivery && (
            <div className="space-y-1.5">
              {renderFieldLabel('Ciudad de entrega', true)}
              <Input
                value={form.ciudadDestino}
                onChange={(e) => {
                  setField('ciudadDestino', e.target.value);
                  clearError('ciudadDestino');
                }}
                placeholder="Dónde se entrega la carga"
                className={errores.ciudadDestino ? 'border-destructive' : ''}
              />
              {renderFieldError('ciudadDestino')}
            </div>
          )}
        </div>
      </div>
    </SectionBlock>
  );

  // 4) Detalle de bultos
  const renderDetalle = () => (
    <SectionBlock
      icon={Package}
      title="Detalle de Bultos"
      number={4}
      modal={modal}
      action={
        <Button type="button" variant="outline" size="sm" onClick={addDetalle}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Añadir fila
        </Button>
      }
    >
      {modal ? (
        // Tabla inline compacta
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
                {/* "Bultos" es un numerito; "Tipo" es un select con
                    etiquetas largas (Pallet, Huacal…) y necesita aire. */}
                <th className="py-2 px-2 font-medium w-[64px]">Bultos</th>
                <th className="py-2 px-2 font-medium w-[132px]">Tipo</th>
                <th className="py-2 px-2 font-medium">Largo</th>
                <th className="py-2 px-2 font-medium">Ancho</th>
                <th className="py-2 px-2 font-medium">Alto</th>
                <th className="py-2 px-2 font-medium">Unidad</th>
                <th className="py-2 px-2 font-medium">Peso x unidad</th>
                <th className="py-2 px-2 font-medium">Und. Peso</th>
                <th className="py-2 px-2 font-medium">m³</th>
                <th className="py-2 px-2 font-medium">Mercancía</th>
                <th className="py-2 px-2 font-medium">Apilable</th>
                <th className="py-2 px-2 font-medium">IMO</th>
                <th className="py-2 px-1"></th>
              </tr>
            </thead>
            <tbody>
              {detalles.map((d, idx) => {
                const detErr = errores.detalles?.[idx] || {};
                return (
                  <tr key={idx} className="border-t border-border align-top">
                    <td className="p-1.5">
                      <Input
                        type="number"
                        min={1}
                        value={d.numPiezas}
                        onChange={(e) => updateDetalle(idx, 'numPiezas', e.target.value)}
                        className={`h-8 text-sm ${detErr.numPiezas ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.tipoBulto}
                        onChange={(e) => updateDetalle(idx, 'tipoBulto', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        {TIPOS_BULTO.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={d.largo}
                        onChange={(e) => updateDetalle(idx, 'largo', e.target.value)}
                        className={`h-8 text-sm ${detErr.largo ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={d.ancho}
                        onChange={(e) => updateDetalle(idx, 'ancho', e.target.value)}
                        className={`h-8 text-sm ${detErr.ancho ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={d.alto}
                        onChange={(e) => updateDetalle(idx, 'alto', e.target.value)}
                        className={`h-8 text-sm ${detErr.alto ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.unidadDimension}
                        onChange={(e) => updateDetalle(idx, 'unidadDimension', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        {UNIDADES_DIMENSION.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={d.peso}
                        onChange={(e) => updateDetalle(idx, 'peso', e.target.value)}
                        className={`h-8 text-sm ${detErr.peso ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.unidadPeso}
                        onChange={(e) => updateDetalle(idx, 'unidadPeso', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        {UNIDADES_PESO.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5">
                      <div className="h-8 flex items-center text-xs font-mono text-muted-foreground tabular-nums">
                        {(totales.volumenPorLinea[idx] ?? 0).toFixed(3)}
                      </div>
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.tipoMercanciaId}
                        onChange={(e) => updateDetalle(idx, 'tipoMercanciaId', e.target.value)}
                        className={`h-8 w-full rounded-md border bg-background px-2 text-sm ${
                          detErr.tipoMercanciaId ? 'border-destructive' : 'border-input'
                        }`}
                      >
                        <option value="">— Tipo —</option>
                        {commodityTypes.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.apilable}
                        onChange={(e) => updateDetalle(idx, 'apilable', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="Si">Sí</option>
                        <option value="No">No</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      <div className="space-y-1">
                        <label className="flex items-center gap-1 text-xs h-8">
                          <input
                            type="checkbox"
                            checked={!!d.imo}
                            onChange={(e) => updateDetalle(idx, 'imo', e.target.checked)}
                            className="h-3.5 w-3.5"
                          />
                          IMO
                        </label>
                        {d.imo && (
                          <>
                            <Input
                              value={d.imoClase}
                              onChange={(e) => updateDetalle(idx, 'imoClase', e.target.value)}
                              placeholder="Clase"
                              maxLength={10}
                              className={`h-7 text-xs ${detErr.imoClase ? 'border-destructive' : ''}`}
                            />
                            <Input
                              value={d.unNumber}
                              onChange={(e) => updateDetalle(idx, 'unNumber', e.target.value)}
                              placeholder="UN1263"
                              maxLength={10}
                              className="h-7 text-xs"
                            />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDetalle(idx)}
                        disabled={detalles.length === 1}
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        title="Eliminar fila"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // Layout standalone con cards
        <div className="space-y-3">
          {detalles.map((d, idx) => {
            const detErr = errores.detalles?.[idx] || {};
            return (
              <div key={idx} className="border border-border rounded-lg p-3 bg-muted/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">
                    Bulto #{idx + 1}
                    <span className="ml-2 text-xs font-mono text-muted-foreground">
                      {(totales.volumenPorLinea[idx] ?? 0).toFixed(3)} m³
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDetalle(idx)}
                    disabled={detalles.length === 1}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Eliminar fila"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Bultos *</Label>
                    <Input
                      type="number"
                      min={1}
                      value={d.numPiezas}
                      onChange={(e) => updateDetalle(idx, 'numPiezas', e.target.value)}
                      className={detErr.numPiezas ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de bulto</Label>
                    <select
                      value={d.tipoBulto}
                      onChange={(e) => updateDetalle(idx, 'tipoBulto', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      {TIPOS_BULTO.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Largo *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.largo}
                      onChange={(e) => updateDetalle(idx, 'largo', e.target.value)}
                      className={detErr.largo ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ancho *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.ancho}
                      onChange={(e) => updateDetalle(idx, 'ancho', e.target.value)}
                      className={detErr.ancho ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Alto *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.alto}
                      onChange={(e) => updateDetalle(idx, 'alto', e.target.value)}
                      className={detErr.alto ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unidad</Label>
                    <select
                      value={d.unidadDimension}
                      onChange={(e) => updateDetalle(idx, 'unidadDimension', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      {UNIDADES_DIMENSION.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Peso unitario *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.peso}
                      onChange={(e) => updateDetalle(idx, 'peso', e.target.value)}
                      className={detErr.peso ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unidad peso</Label>
                    <select
                      value={d.unidadPeso}
                      onChange={(e) => updateDetalle(idx, 'unidadPeso', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      {UNIDADES_PESO.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Apilable</Label>
                    <select
                      value={d.apilable}
                      onChange={(e) => updateDetalle(idx, 'apilable', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="Si">Sí</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <Label className="text-xs">Tipo de mercancía *</Label>
                  <select
                    value={d.tipoMercanciaId}
                    onChange={(e) => updateDetalle(idx, 'tipoMercanciaId', e.target.value)}
                    className={`flex h-10 w-full rounded-md border bg-background px-2 text-sm ${
                      detErr.tipoMercanciaId ? 'border-destructive' : 'border-input'
                    }`}
                  >
                    <option value="">— Selecciona —</option>
                    {commodityTypes.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mercancía peligrosa (IMDG / IMO) */}
                <div className="mt-3 rounded-md border border-border bg-card p-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!d.imo}
                      onChange={(e) => updateDetalle(idx, 'imo', e.target.checked)}
                      className="h-4 w-4"
                    />
                    Mercancía peligrosa (IMDG / IMO)
                  </label>
                  {d.imo && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Clase IMO *</Label>
                        <Input
                          value={d.imoClase}
                          onChange={(e) => updateDetalle(idx, 'imoClase', e.target.value)}
                          placeholder="Ej. 3, 8"
                          maxLength={10}
                          className={detErr.imoClase ? 'border-destructive' : ''}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Número UN</Label>
                        <Input
                          value={d.unNumber}
                          onChange={(e) => updateDetalle(idx, 'unNumber', e.target.value)}
                          placeholder="Ej. UN1263"
                          maxLength={10}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <Button type="button" variant="outline" onClick={addDetalle} className="w-full">
            <Plus className="mr-2 h-4 w-4" /> Agregar bulto
          </Button>
          {errores.detallesGeneral && <FieldError msg={errores.detallesGeneral} />}
        </div>
      )}
    </SectionBlock>
  );

  // 5) Carga — todos los totales son de solo lectura: los calcula el
  // detalle y los recalcula el backend.
  const renderCarga = () => (
    <SectionBlock icon={Ship} title="Carga" number={5} modal={modal}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('Bultos', true)}
          <Input value={totales.numPiezas} readOnly />
          {renderFieldError('numPiezas')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Peso total (kg)', true)}
          <Input value={totales.pesoTotal.toFixed(2)} readOnly />
          {renderFieldError('pesoTotal')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Volumen (m³ / CBM)', true)}
          <Input value={totales.volumenTotal.toFixed(3)} readOnly />
          {renderFieldError('volumenTotal')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Toneladas')}
          <Input value={totales.toneladasTotal.toFixed(3)} readOnly />
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Unidades W/M')}
          <Input
            value={totales.unidadesWM.toFixed(3)}
            readOnly
            className="font-semibold"
          />
        </div>
      </div>
      <div className="text-xs text-muted-foreground mt-2 rounded-md border border-border bg-muted/30 px-3 py-2">
        Las <strong>unidades W/M</strong> (weight or measurement) son lo que factura la
        naviera: el mayor entre el volumen en m³ y el peso en toneladas, con un mínimo
        de 1. Aquí:{' '}
        <span className="font-mono text-foreground">
          máx({totales.volumenTotal.toFixed(3)} m³, {totales.toneladasTotal.toFixed(3)} t, 1) ={' '}
          {totales.unidadesWM.toFixed(3)}
        </span>
        .
      </div>
    </SectionBlock>
  );

  // ============ Layout modal vs normal ============
  if (modal) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[85vh]">
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4 flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold leading-tight">{modalTitle}</h2>
            <p className="text-xs text-muted-foreground">{modalSubtitle}</p>
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {renderInfoGeneral()}
          {renderClienteAsesor()}
          {renderRutaMaritima()}
          {renderDetalle()}
          {renderCarga()}
          {/* Detalle de lo que falta: al final del formulario, para que
              no ocupe pantalla mientras se diligencia. */}
          {renderCamposFaltantes()}
        </div>

        {/* Footer sticky: solo el contador en una línea + los botones,
            para no robarle alto al formulario. */}
        <div className="sticky bottom-0 z-10 bg-background border-t border-border px-6 py-4 flex flex-wrap items-center justify-between gap-2">
          {camposFaltantes.length > 0 ? (
            <span className="text-xs text-destructive inline-flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Faltan {camposFaltantes.length} campo(s) — el detalle está al final
              del formulario
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !isFormComplete}
              title={!isFormComplete ? 'Completa todos los campos requeridos' : undefined}
            >
              <Save className="mr-2 h-4 w-4" />
              {submitting ? 'Guardando…' : submitLabel}
            </Button>
          </div>
        </div>
      </form>
    );
  }

  // Layout normal (con SectionCards)
  const wrapperClass = compact ? 'space-y-3' : 'space-y-4';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={wrapperClass}>
      <form onSubmit={handleSubmit} className={wrapperClass}>
        {!hideHeader && (
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Nueva Solicitud LCL</h1>
              <p className="text-sm text-muted-foreground">
                Complete las 5 secciones. Todos los catálogos se consultan al backend.
              </p>
            </div>
          </div>
        )}

        {renderInfoGeneral()}
        {renderClienteAsesor()}
        {renderRutaMaritima()}
        {renderDetalle()}
        {renderCarga()}

        {renderCamposFaltantes()}

        <div className="flex justify-end gap-2 pt-2">
          {showCancelButton && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            <Save className="mr-2 h-4 w-4" />
            {submitting ? 'Guardando…' : submitLabel}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}

// ============ Sub-componentes de layout ============

function SectionBlock({ icon: Icon, title, number, children, modal, action }) {
  if (modal) {
    return (
      <section className="space-y-3">
        <header className="flex items-center justify-between border-b border-border pb-2">
          <div className="flex items-center gap-2">
            {Icon && (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
            )}
            <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
              {title}
            </h3>
          </div>
          {action}
        </header>
        <div>{children}</div>
      </section>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
            {number}
          </span>
          {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function FieldError({ msg }) {
  return (
    <div className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3 w-3" />
      <span>{msg}</span>
    </div>
  );
}
